import { useState } from 'react';
import {
  format, parse, isValid, addMonths, subMonths,
  startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, isSameMonth, isSameDay, isToday, isBefore, isAfter,
} from 'date-fns';
import { useDateLocale } from '../../utils/dateLocale';

interface Props {
  /** yyyy-MM-dd, or null while nothing is picked yet. */
  start: string | null;
  end: string | null;
  onChange: (start: string, end: string | null) => void;
  /** Earliest selectable day. Defaults to today — trips are not planned backwards. */
  minDate?: Date;
}

const ISO = 'yyyy-MM-dd';

function toDate(value: string | null): Date | null {
  if (!value) return null;
  const parsed = parse(value, ISO, new Date());
  return isValid(parsed) ? parsed : null;
}

function startOfToday(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

/**
 * Inline two-click range calendar. Separate from `DatePicker`, which is a
 * single-date popup wired into the forms — the interaction models share
 * nothing beyond the grid, and reworking DatePicker into both would put every
 * form in the app at risk for one new step.
 */
export default function DateRangeCalendar({ start, end, onChange, minDate }: Props) {
  const locale = useDateLocale();
  const from = toDate(start);
  const to = toDate(end);
  const floor = minDate ?? startOfToday();

  const [cursor, setCursor] = useState<Date>(from ?? floor);
  const [hovered, setHovered] = useState<Date | null>(null);

  const weekdays = eachDayOfInterval({
    start: startOfWeek(new Date(), { weekStartsOn: 1 }),
    end:   endOfWeek(new Date(), { weekStartsOn: 1 }),
  }).map(d => format(d, 'EEEEEE', { locale }));

  const days = eachDayOfInterval({
    start: startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 }),
    end:   endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 }),
  });

  // While picking the second day, the hovered one stands in for it so the
  // range fills in under the cursor.
  const pendingEnd = to ?? (from && hovered && isAfter(hovered, from) ? hovered : null);

  function selectDay(day: Date) {
    // A click before the current start (or a click on it) restarts the range
    // rather than rejecting the input.
    if (!from || to || isBefore(day, from) || isSameDay(day, from)) {
      onChange(format(day, ISO), null);
      return;
    }
    onChange(format(from, ISO), format(day, ISO));
  }

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-2.5">
      <div className="flex items-center justify-between mb-2">
        <button
          type="button" onClick={() => setCursor(c => subMonths(c, 1))}
          className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" /></svg>
        </button>
        <span className="font-mono text-[10px] font-semibold tracking-widest uppercase text-slate-700 dark:text-slate-200">
          {format(cursor, 'MMMM yyyy', { locale })}
        </span>
        <button
          type="button" onClick={() => setCursor(c => addMonths(c, 1))}
          className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" /></svg>
        </button>
      </div>

      <div className="grid grid-cols-7">
        {weekdays.map(d => (
          <div key={d} className="text-center font-mono text-[9px] font-semibold tracking-widest uppercase text-slate-400 py-0.5">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7" onMouseLeave={() => setHovered(null)}>
        {days.map(day => {
          const disabled  = isBefore(day, floor);
          const isStart   = from ? isSameDay(day, from) : false;
          const isEnd     = pendingEnd ? isSameDay(day, pendingEnd) : false;
          const inRange   = !!from && !!pendingEnd && isAfter(day, from) && isBefore(day, pendingEnd);
          const edge      = isStart || isEnd;
          const thisMonth = isSameMonth(day, cursor);

          return (
            <button
              key={day.toISOString()} type="button" disabled={disabled}
              onClick={() => selectDay(day)}
              onMouseEnter={() => setHovered(day)}
              className={`h-7 w-full font-mono text-[11px] transition-colors cursor-pointer
                ${inRange ? 'bg-teal-50 dark:bg-teal-900/30' : ''}
                ${isStart && pendingEnd ? 'rounded-l-lg' : ''}
                ${isEnd && from ? 'rounded-r-lg' : ''}
                ${edge && !(isStart && pendingEnd) && !(isEnd && from) ? 'rounded-lg' : ''}
                ${edge      ? 'bg-teal-500 text-white font-semibold'
                : inRange   ? 'text-teal-700 dark:text-teal-300'
                : isToday(day) ? 'rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 font-semibold'
                : thisMonth ? 'rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                :             'rounded-lg text-slate-300 dark:text-slate-600'}
                ${disabled ? 'opacity-30 pointer-events-none' : ''}
              `}
            >
              {format(day, 'd')}
            </button>
          );
        })}
      </div>
    </div>
  );
}
